import React from 'react';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../../contexts/CartContext';
import { getBookImageUrl } from '../../common/Utils/BookImages';
import ROUTES from '../../common/Routing/Routes';
import { getItemLink, isMerchandiseItem } from '../../common/Utils/ItemLinks';
import CartUpsell from './CartUpsell';
import './CartPage.css';

const formatPrice = (value: number) => `${Number(value || 0).toFixed(2)} грн`;

function CartPage() {
  const { cartItems, cartTotal, closeCartModal, removeFromCart, setItemCount, clearCart } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    closeCartModal();
    navigate(ROUTES.CHECKOUT);
  };

  if (cartItems.length === 0) {
    return (
      <Box className="cart cart-empty">
        <MenuBookOutlinedIcon fontSize="large" />
        <Typography variant="h6">Кошик оренди порожній</Typography>
        <Typography variant="body2">Додайте книжки, які хочете взяти в оренду.</Typography>
      </Box>
    );
  }

  return (
    <Box className="cart">
      <Box className="cart-list">
        {cartItems.map((item) => {
          const count = item.count || 1;
          const maxCount = item.stockCount > 0 ? item.stockCount : 1;
          const isAtStockLimit = count >= maxCount;

          return (
            <Box key={item.id} className="cart-item">
              <Link
                to={getItemLink(item)}
                className="cart-item-image-link"
                onClick={closeCartModal}
                aria-label={`Відкрити ${item.name}`}
              >
                <img src={getBookImageUrl(item)} alt={item.name} className="cart-item-image" />
              </Link>

              <Box className="cart-item-info">
                <Typography
                  component={Link}
                  to={getItemLink(item)}
                  onClick={closeCartModal}
                  variant="subtitle1"
                  className="cart-item-title cart-item-title-link"
                >
                  {item.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatPrice(item.price)} x {count}
                </Typography>
                <Typography variant="caption" color={isAtStockLimit ? 'warning.main' : 'text.secondary'}>
                  Доступно: {maxCount} шт.
                </Typography>
              </Box>

              <Box className="cart-quantity" aria-label={`Кількість ${item.name}`}>
                <IconButton size="small" onClick={() => setItemCount(item.id, count - 1)}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <span>{count}</span>
                <IconButton
                  size="small"
                  onClick={() => setItemCount(item.id, count + 1)}
                  disabled={isAtStockLimit}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>

              <Typography className="cart-item-total">{formatPrice(item.price * count)}</Typography>

              <IconButton aria-label="Прибрати з кошика оренди" onClick={() => removeFromCart(item.id)}>
                <CloseIcon />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      <Box className="cart-actions">
        <Button size="small" color="inherit" onClick={clearCart}>
          Очистити кошик
        </Button>
      </Box>

      {!cartItems.every(isMerchandiseItem) && <CartUpsell />}

      <Divider />

      <Box className="cart-summary">
        <Box>
          <Typography variant="body2" color="text.secondary">
            {cartItems.some(isMerchandiseItem) ? 'Разом' : 'Донат за оренду'}
          </Typography>
          <Typography variant="h6">{formatPrice(cartTotal)}</Typography>
          <Typography variant="caption" color="text.secondary" className="cart-summary-note">
            Один раз, без щомісячних платежів і жорсткого терміну. Доставка до вас — окремо, зворотна — за наш рахунок.
          </Typography>
        </Box>
        <Button variant="contained" size="large" onClick={handleCheckout}>
          Оформити оренду
        </Button>
      </Box>
    </Box>
  );
}

export default CartPage;
